<template>
    <v-list ref="list" class="client-list">
        <v-virtual-scroll
            :items="filtered"
            :item-height="70"
            :height="height"
            :bench="10"
        >
            <template v-slot="{ item }">
                <v-list-item
                    :class="{ item: true, selected: selectedClient && selectedClient.id === item.client.id }"
                    @click="select(item)"
                >
                    <v-list-item-content>
                        <v-list-item-title
                            class="d-flex align-center"
                            :class="{ 'client-disabled': item.disabled }"
                        >
                            {{ item.client.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-caption">
                            <span v-if="item.client.address">{{ `${item.client.address}` }}</span>
                            <span v-if="item.client.city"> {{ `${item.client.city}` }}</span>
                            <span v-if="item.client.country"> {{ `${item.client.country}` }}</span>
                            <div v-if="(!item.client.country || item.client.country === 'MNE') && item.client.tax_id_number">
                                {{ `${$t('general.tax_id_number')}: ${item.client.tax_id_number}` }}
                            </div>
                            <div v-if="(item.client.country && item.client.country !== 'MNE') && item.client.id_number">
                                {{ `${$t('general.id_number')}: ${item.client.id_number}` }}
                            </div>
                            <strong v-if="item.client.getDebt().toUnit() > 0" class="error--text">
                                {{ `${$t('general.installments_debt')}: ${item.client.getDebt().toFormat()}` }}
                            </strong>
                        </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-icon class="align-center">
                        <v-scale-transition hide-on-leave>
                            <v-icon
                                v-if="selectedClient && selectedClient.id === item.client.id"
                                class="mr-2"
                                :size="24"
                                color="success"
                            >
                                far fa-check
                            </v-icon>
                        </v-scale-transition>

                        <v-menu
                            v-if="allowEdit || allowPayments"
                            bottom
                            transition="slide-y-transition"
                        >
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" icon>
                                    <v-icon>far fa-ellipsis-h</v-icon>
                                </v-btn>
                            </template>

                            <v-list>
                                <v-list-item
                                    v-if="allowEdit"
                                    :key="1"
                                    @click="$emit('edit-client', item.client)"
                                >
                                    <v-list-item-action class="ml-1">
                                        <v-icon>fal fa-pen</v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>{{ $t('general.edit') }}</v-list-item-title>
                                </v-list-item>

                                <v-list-item
                                    v-if="allowPayments && item.client.getDebt().toUnit() > 0"
                                    :key="2"
                                    @click="$emit('add-payment', item.client)"
                                >
                                    <v-list-item-action class="ml-1">
                                        <v-icon>fal fa-money-check-alt</v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>{{ $t('general.installments_pay') }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-list-item-icon>

                </v-list-item>
            </template>
        </v-virtual-scroll>
    </v-list>
</template>

<script>
import Client from '@/models/Client';
import { mapGetters } from 'vuex';

export default {
    props: {
        filterQuery: {
            type: String,
            default: null
        },
        selectedClient: {
            type: Object,
            default: () => null
        },
        allowEdit: {
            type: Boolean,
            default: true
        },
        allowPayments: {
            type: Boolean,
            default: true
        }
    },

    mounted() {
        this.filter();

        this.$nextTick(() => {
            const height = this.$refs.list?.$el?.offsetHeight
            if(height) {
                this.height = height
            }
        })
    },

    data: () => ({
        filtered: [],
        height: 350,
    }),

    watch: {
        clients() {
            this.filter();
        },

        filterQuery() {
            this.filter();
        },
    },

    computed: {
        ...mapGetters(['curOrder']),

        clients() {
            return Client.all();
        },

        orderEmpty() {
            return !this.curOrder || !this.curOrder.items || !this.curOrder.items.length;
        },
    },

    methods: {
        select(item) {
            if(item.disabled && this.selectedClient?.id !== item.client.id) {
                this.$snackbar(this.$t('order.buyer_card_needed'), 'error');
                return
            }

            if(this.orderEmpty && this.isAllegra) return

            this.$emit('update:selected-client', !this.selectedClient || this.selectedClient.id !== item.client.id ? item.client : null);
        },

        filter: _.debounce(function() {
            let filtered = [];

            this.clients.forEach(client => {
                const item = {
                    client,
                    disabled: client.getDiscountPercent() > 0 && client.card && this.filterQuery !== client.card
                }

                if (this.selectedClient && this.selectedClient.id === client.id) {
                    filtered.unshift(item);
                } else {
                    filtered.push(item);
                }
            });

            if (!this.filterQuery) {
                this.filtered = filtered;
                return;
            }

            const clientCardFound = filtered.find(item => item.client.card === this.filterQuery)
            if (clientCardFound) {
                this.$emit('update:filter-query', null)
                this.filtered = [clientCardFound]
                if (this.selectedClient?.id !== clientCardFound.client.id) {
                    this.select(clientCardFound)
                }
                return
            }

            this.filtered = filtered.filter(item => {
                return this.selectedClient?.id === item.client.id || item.client.matches(this.filterQuery)
            });
        }, 300)
    }
};
</script>

<style lang="scss" scoped>
.client-list {
    padding: 0;
    width: 100%;

    .client-disabled {
        opacity: 0.4;
    }
}
.item {
    display: flex;
    align-items: center;
    height: 70px;
    border-bottom: 1px solid lightgray;

    ::v-deep .v-list-item__icon {
        align-self: initial;
    }
}

.selected {
    background: #efefef;

    .v-list-item__icon {
        margin: 0;
        height: 48px;
    }
}
</style>
